<template>
  <div class="customer-details-mobile bg-[#f4f6fc] min-h-screen flex flex-col">
    <!-- Mobile Header Bar -->
    <div class="flex items-center justify-between px-4 py-3 bg-white border-b sticky top-0 z-10 shadow-sm">
      <div class="flex items-center gap-3">
        <v-avatar size="36" class="border-2 border-primary shadow-sm">
          <img v-if="avatarUrl" :src="avatarUrl" @error="onAvatarError" alt="Avatar" />
          <img v-else :src="fallbackAvatar" alt="Avatar" />
        </v-avatar>
        <div>
          <h3 class="text-sm font-bold text-gray-800 uppercase leading-tight">
            {{ customer.customerFullName || `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || 'N/A' }}
          </h3>
          <span class="text-[10px] text-gray-500 font-semibold">CRN: {{ customer.crn || '-' }}</span>
        </div>
      </div>
      <v-btn icon small @click="closeCustomerPopUp">
        <v-icon size="20">mdi-close</v-icon>
      </v-btn>
    </div>

    <!-- Loan Summary Chips -->
    <div v-if="Object.keys(statusCounts).length > 0" class="px-4 py-3 bg-white border-b">
      <div class="flex items-center justify-between mb-2">
        <span class="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Loan Summary</span>
        <span class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Total: {{ totalLoansCount }}</span>
      </div>
      <div class="flex flex-wrap gap-1">
        <v-chip
          v-for="(count, status) in statusCounts"
          :key="status"
          x-small
          label
          class="font-black px-2"
          :color="getStatusInfo(status).color"
          dark
        >
          <v-icon x-small left>{{ getStatusInfo(status).icon }}</v-icon>
          {{ status }}: {{ count }}
        </v-chip>
      </div>
    </div>

    <!-- Tab Selector Dropdown -->
    <div class="px-4 pt-3 pb-1 bg-white border-b">
      <v-select
        v-model="activeTab"
        :items="tabItems"
        item-text="label"
        item-value="value"
        outlined
        dense
        hide-details
        class="rounded-lg"
        prepend-inner-icon="mdi-tab"
      ></v-select>
    </div>

    <!-- Tab Contents -->
    <div class="px-4 pt-4 pb-8 flex-grow overflow-y-auto">

      <!-- Tab 0: Basic Info -->
      <div v-if="activeTab === 0">
        <div class="flex flex-col gap-3">
          <div v-for="(row, i) in basicInfoRows" :key="i" class="flex justify-between py-2 border-b border-gray-100">
            <span class="text-xs font-bold text-gray-500 w-2/5">{{ row.label }}</span>
            <span class="text-xs text-gray-800 font-semibold text-right w-3/5 break-words" :class="row.class">{{ row.value }}</span>
          </div>
        </div>
      </div>

      <!-- Tab 1: Communication -->
      <div v-else-if="activeTab === 1">
        <h4 class="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Contact Info</h4>
        <div class="flex flex-col gap-2 mb-5">
          <div class="flex justify-between py-2 border-b border-gray-100">
            <span class="text-xs font-bold text-gray-500 flex items-center gap-1">
              <v-icon small color="primary">mdi-email</v-icon> Email
            </span>
            <span class="text-xs text-gray-800 font-semibold text-right break-all">{{ customer.customerEmail || 'N/A' }}</span>
          </div>
          <div
            v-for="(num, idx) in customer.customerMobile"
            :key="idx"
            class="flex justify-between py-2 border-b border-gray-100"
          >
            <span class="text-xs font-bold text-gray-500 flex items-center gap-1">
              <v-icon small color="primary">mdi-phone</v-icon> Mobile {{ idx + 1 }}
            </span>
            <span class="text-xs text-gray-800 font-semibold">{{ mask(num) }}</span>
          </div>
          <div v-if="!customer.customerMobile || customer.customerMobile.length === 0" class="text-xs text-gray-400 italic py-2">
            No mobile numbers provided.
          </div>
        </div>

        <h4 class="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Addresses</h4>
        <div v-if="customer.customerAddress && customer.customerAddress.length > 0" class="flex flex-col gap-3">
          <div
            v-for="address in customer.customerAddress"
            :key="address._id"
            class="border rounded-xl p-4 bg-white shadow-sm"
          >
            <div class="font-bold text-primary mb-2 flex items-center justify-between">
              <span class="text-sm">{{ convertTitle(address.typeOfAddress) }}</span>
              <v-btn icon x-small color="blue" @click="openGoogleMaps(address)">
                <v-icon size="16">mdi-map-marker-radius</v-icon>
              </v-btn>
            </div>
            <div class="text-xs text-gray-700 leading-relaxed">{{ extractAddress(address) }}</div>
          </div>
        </div>
        <div v-else class="text-xs text-gray-400 italic py-2">No addresses registered.</div>
      </div>

      <!-- Tab 2: Employment and Income -->
      <div v-else-if="activeTab === 2">
        <div v-if="customer.employmentDetails">
          <div class="flex flex-col gap-2 mb-4">
            <div class="flex justify-between py-2 border-b border-gray-100">
              <span class="text-xs font-bold text-gray-500">Job Type</span>
              <span class="text-xs font-semibold text-gray-800">{{ convertTitle(customer.employmentDetails.empType) || 'N/A' }}</span>
            </div>
            <div class="flex justify-between py-2 border-b border-gray-100">
              <span class="text-xs font-bold text-gray-500">Employer</span>
              <span class="text-xs font-semibold text-gray-800 uppercase">{{ customer.employmentDetails.workHistory?.[0]?.employerName || 'N/A' }}</span>
            </div>
            <div class="flex justify-between py-2 border-b border-gray-100">
              <span class="text-xs font-bold text-gray-500">Designation</span>
              <span class="text-xs font-semibold text-gray-800">{{ customer.employmentDetails.workHistory?.[0]?.designation || 'N/A' }}</span>
            </div>
            <div class="flex justify-between py-2 border-b border-gray-100">
              <span class="text-xs font-bold text-gray-500">Experience</span>
              <span class="text-xs font-semibold text-gray-800">{{ customer.employmentDetails.totalWorkExperience ? `${customer.employmentDetails.totalWorkExperience} Years` : 'N/A' }}</span>
            </div>
            <div class="flex justify-between py-2 border-b border-gray-100">
              <span class="text-xs font-bold text-gray-500">Joining Date</span>
              <span class="text-xs font-semibold text-gray-800">{{ formatDate(customer.employmentDetails.joiningDate) }}</span>
            </div>
          </div>
          <!-- Income Summary Cards -->
          <div class="grid grid-cols-3 gap-2 mt-2">
            <div class="bg-blue-50 rounded-xl p-3 text-center border border-blue-100">
              <div class="text-[9px] text-blue-600 font-bold uppercase tracking-wide mb-1">Monthly Income</div>
              <div class="text-sm font-bold text-blue-700">{{ customer.employmentDetails.monthlyIncome ? `₹${Number(customer.employmentDetails.monthlyIncome).toLocaleString()}` : 'N/A' }}</div>
            </div>
            <div class="bg-red-50 rounded-xl p-3 text-center border border-red-100">
              <div class="text-[9px] text-red-600 font-bold uppercase tracking-wide mb-1">EMI Outgo</div>
              <div class="text-sm font-bold text-red-600">{{ customer.employmentDetails.totalEMIAmountPerMonth ? `₹${Number(customer.employmentDetails.totalEMIAmountPerMonth).toLocaleString()}` : 'N/A' }}</div>
            </div>
            <div class="bg-green-50 rounded-xl p-3 text-center border border-green-100">
              <div class="text-[9px] text-green-600 font-bold uppercase tracking-wide mb-1">Net Take Home</div>
              <div class="text-sm font-bold text-green-700">{{ customer.employmentDetails.totalIncome ? `₹${Number(customer.employmentDetails.totalIncome).toLocaleString()}` : 'N/A' }}</div>
            </div>
          </div>
        </div>
        <div v-else class="flex flex-col gap-2">
          <div class="flex justify-between py-2 border-b border-gray-100">
            <span class="text-xs font-bold text-gray-500">Job Type</span>
            <span class="text-xs font-semibold text-gray-800">{{ convertTitle(customer.jobType) || 'N/A' }}</span>
          </div>
          <div class="flex justify-between py-2 border-b border-gray-100">
            <span class="text-xs font-bold text-gray-500">Occupation</span>
            <span class="text-xs font-semibold text-gray-800">{{ customer.occupation || 'N/A' }}</span>
          </div>
          <div class="flex justify-between py-2 border-b border-gray-100">
            <span class="text-xs font-bold text-gray-500">Company</span>
            <span class="text-xs font-semibold text-gray-800">{{ customer.company || 'N/A' }}</span>
          </div>
          <div class="flex justify-between py-2 border-b border-gray-100 bg-green-50 rounded px-2">
            <span class="text-xs font-bold text-gray-600">Monthly Salary</span>
            <span class="text-sm font-bold text-green-700">{{ customer.salary ? `₹${Number(customer.salary).toLocaleString()}` : 'N/A' }}</span>
          </div>
        </div>
      </div>

      <!-- Tab 3: Identity Details -->
      <div v-else-if="activeTab === 3">
        <div class="flex items-center justify-between mb-4">
          <span class="text-xs font-bold text-gray-500 uppercase tracking-wider">Identity Documents</span>
          <v-btn
            v-if="customer.identityDetails && customer.identityDetails.length > 0"
            x-small
            :color="identityRevealed ? 'grey darken-1' : 'primary'"
            outlined
            :loading="loadingIdentity"
            class="normal-case rounded-lg font-bold"
            @click="toggleIdentityReveal"
          >
            <v-icon left x-small>{{ identityRevealed ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
            {{ identityRevealed ? 'Hide' : 'View' }}
          </v-btn>
        </div>
        <div v-if="customer.identityDetails && customer.identityDetails.length > 0" class="flex flex-col gap-4">
          <div
            v-for="(id, idx) in customer.identityDetails"
            :key="idx"
            class="border rounded-xl p-4 bg-white shadow-sm"
          >
            <div class="flex items-center justify-between mb-3">
              <div class="flex items-center gap-2">
                <v-icon color="primary" size="18">mdi-card-account-details-outline</v-icon>
                <span class="text-sm font-bold text-gray-800">{{ id.idType }}</span>
              </div>
              <v-chip x-small :color="id.isVerfied ? 'success' : 'warning'" text-color="white" class="font-bold">
                {{ id.isVerfied ? 'VERIFIED' : 'PENDING' }}
              </v-chip>
            </div>
            <div class="flex flex-col gap-2 text-xs">
              <div class="flex justify-between py-1 border-b border-gray-100">
                <span class="text-gray-500 font-bold">ID Number</span>
                <span class="font-mono font-semibold" v-if="identityRevealed && getRevealedId(id.idType)">{{ getRevealedId(id.idType) }}</span>
                <span class="text-gray-400 font-mono" v-else>•••• •••• ••••</span>
              </div>
              <div class="flex justify-between py-1 border-b border-gray-100">
                <span class="text-gray-500 font-bold">Name on ID</span>
                <span class="font-semibold text-gray-800 text-right">{{ id.nameAsperId || customer.customerFullName || 'N/A' }}</span>
              </div>
              <div class="flex justify-between py-1 border-b border-gray-100">
                <span class="text-gray-500 font-bold">Issued By</span>
                <span class="font-semibold text-gray-800">{{ id.issuedBy || 'N/A' }}</span>
              </div>
              <div class="flex justify-between py-1">
                <span class="text-gray-500 font-bold">Validity</span>
                <span class="font-semibold text-gray-800">{{ formatDate(id.issueDate) }} – {{ formatDate(id.expiryDate) }}</span>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="flex flex-col items-center justify-center py-10 text-center">
          <v-icon size="48" color="grey lighten-2">mdi-id-card</v-icon>
          <p class="mt-3 text-xs text-gray-400 italic">No identity documents provided.</p>
        </div>
      </div>

      <!-- Tab 4: Bank Details -->
      <div v-else-if="activeTab === 4">
        <div v-if="customer.bankDetails" class="flex flex-col gap-2">
          <div class="flex justify-between py-2 border-b border-gray-100">
            <span class="text-xs font-bold text-gray-500">Bank Name</span>
            <span class="text-xs font-semibold text-gray-800 uppercase">{{ customer.bankDetails.bankName || 'N/A' }}</span>
          </div>
          <div class="flex justify-between py-2 border-b border-gray-100">
            <span class="text-xs font-bold text-gray-500">Account No</span>
            <span class="text-xs font-mono font-bold text-gray-800">{{ customer.bankDetails.accountNumber || 'N/A' }}</span>
          </div>
          <div class="flex justify-between py-2 border-b border-gray-100">
            <span class="text-xs font-bold text-gray-500">IFSC Code</span>
            <span class="text-xs font-bold text-blue-700">{{ customer.bankDetails.ifscCode || 'N/A' }}</span>
          </div>
          <div class="flex justify-between py-2 border-b border-gray-100">
            <span class="text-xs font-bold text-gray-500">Account Type</span>
            <span class="text-xs font-semibold text-gray-800">{{ customer.bankDetails.accountType || 'N/A' }}</span>
          </div>
          <div class="flex justify-between py-2 border-b border-gray-100">
            <span class="text-xs font-bold text-gray-500">Branch Name</span>
            <span class="text-xs font-semibold text-gray-800 uppercase">{{ customer.bankDetails.branchName || 'N/A' }}</span>
          </div>
          <div class="flex justify-between py-2 border-b border-gray-100">
            <span class="text-xs font-bold text-gray-500">MICR Code</span>
            <span class="text-xs font-semibold text-gray-800">{{ customer.bankDetails.micrCode || 'N/A' }}</span>
          </div>
          <div class="flex justify-between py-2 border-b border-gray-100">
            <span class="text-xs font-bold text-gray-500">GSTN Number</span>
            <span class="text-xs font-semibold text-gray-800">{{ customer.bankDetails.gstnNumber || 'N/A' }}</span>
          </div>
          <div class="flex justify-between py-2 border-b border-gray-100" v-if="customer.bankDetails.phoneNo">
            <span class="text-xs font-bold text-gray-500">Phone No</span>
            <span class="text-xs font-semibold text-gray-800">{{ customer.bankDetails.phoneNo }}</span>
          </div>
        </div>
        <div v-else class="flex flex-col items-center justify-center py-10 text-center">
          <v-icon size="48" color="grey lighten-2">mdi-bank</v-icon>
          <p class="mt-3 text-xs text-gray-400 italic">No bank details provided.</p>
        </div>
      </div>

      <!-- Tab 5: Case History -->
      <div v-else-if="activeTab === 5">
        <div v-if="loadingLoans" class="flex justify-center py-10">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>
        <div v-else-if="groupedLoans && groupedLoans.length > 0">
          <div v-for="group in groupedLoans" :key="group.subCaseId" class="mb-5">
            <div class="flex items-center mb-3">
              <span class="text-xs font-bold text-blue-800 uppercase tracking-wider">{{ group.subCaseName }}</span>
              <v-divider class="ml-3"></v-divider>
            </div>
            <div class="flex flex-col gap-3">
              <div
                v-for="loan in group.loans"
                :key="loan._id"
                class="border rounded-xl p-4 bg-white shadow-sm"
                :class="loan.isDeleted ? 'opacity-50 bg-red-50/30' : ''"
              >
                <div class="flex items-center justify-between mb-2">
                  <span class="text-sm font-bold text-blue-700" :class="{ 'line-through text-red-600': loan.isDeleted }">
                    {{ loan.caseNo }}
                    <v-chip v-if="loan.isDeleted" x-small color="red" dark label class="ml-1 font-bold">Deleted</v-chip>
                  </span>
                  <v-chip
                    x-small
                    :color="getStatusInfo(loan.loanDetails?.currentStatus || loan.status).color"
                    text-color="white"
                    class="font-bold"
                  >
                    {{ loan.loanDetails?.currentStatus || loan.status }}
                  </v-chip>
                </div>
                <div class="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  <div>
                    <span class="text-gray-400 block">Type</span>
                    <span class="font-semibold text-gray-700">{{ loan.loanDetails?.typeOfLoan || loan.caseType || '-' }}</span>
                  </div>
                  <div>
                    <span class="text-gray-400 block">Sanctioned Amt</span>
                    <span class="font-bold text-green-700">₹{{ (loan.loanDetails?.loanAmountSanctioned || 0).toLocaleString() }}</span>
                  </div>
                  <div>
                    <span class="text-gray-400 block">Tenure</span>
                    <span class="font-semibold text-gray-700">{{ loan.loanDetails?.tenure || '-' }} {{ loan.loanDetails?.tenureType || '' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="flex flex-col items-center justify-center py-10 text-center">
          <v-icon size="48" color="grey lighten-2">mdi-text-box-search-outline</v-icon>
          <p class="mt-3 text-xs text-gray-400 italic">No loan records found.</p>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import CustomerDetails from './CustomerDetails.vue'

export default {
  name: 'CustomerDetails_mobile',
  extends: CustomerDetails,
  computed: {
    tabItems() {
      return [
        { label: 'Basic Info', value: 0 },
        { label: 'Communication', value: 1 },
        { label: 'Employment & Income', value: 2 },
        { label: 'Identity Details', value: 3 },
        { label: 'Bank Details', value: 4 },
        { label: 'Case History', value: 5 }
      ]
    },
    basicInfoRows() {
      return [
        { label: 'Name', value: this.customer.customerFullName || 'N/A', class: 'uppercase' },
        { label: 'CIF ID / CRN', value: this.customer.crn || 'N/A' },
        { label: 'Date of Birth', value: this.formatDate(this.customer.dateOfBirth) },
        { label: 'Age', value: this.calculateAge(this.customer.dateOfBirth) },
        { label: 'Gender', value: this.convertTitle(this.customer.gender) || 'N/A' },
        { label: 'Mobile Number', value: this.mask(this.customer.customerMobile?.[0]) || 'N/A' },
        { label: 'Father / Spouse', value: this.customer.fatherName || 'N/A', class: 'uppercase' },
        { label: 'Created By', value: this.formatUserName(this.customer.createdBy) },
        { label: 'Created On', value: this.formatDateTime(this.customer.createdAt) },
        { label: 'Updated By', value: this.formatUserName(this.customer.updatedBy) },
        { label: 'Updated On', value: this.formatDateTime(this.customer.updatedAt) }
      ]
    }
  }
}
</script>

<style scoped>
.customer-details-mobile {
  font-family: 'Inter', sans-serif;
}
</style>
